import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { signupFormService } from '../services/signupFormService';
import { SignupForm, ContentElement, FormBlock } from '../types/signupForm';

function renderContentBlock(element: FormBlock, textColor?: string) {
  const color = textColor || '#111827';
  const elType = element.elementType || (element as any).type;
  switch (elType) {
    case 'heading':
      return (
        <h2 key={element.id} className="text-xl font-bold mb-2" style={{ color }}>
          {element.heading || element.text || 'Heading'}
        </h2>
      );
    case 'text':
      return (
        <p key={element.id} className="text-sm leading-relaxed mb-3 whitespace-pre-wrap" style={{ color }}>
          {element.text || element.content || ''}
        </p>
      );
    case 'image':
      return element.imageUrl ? (
        <div key={element.id} className="mb-3">
          <img src={element.imageUrl} alt={element.content || ''} className="max-w-full rounded" />
        </div>
      ) : null;
    case 'divider':
      return <hr key={element.id} className="my-4 border-gray-200" />;
    case 'button':
      return (
        <div key={element.id} className="mb-3">
          <a
            href={element.buttonUrl || '#'}
            className="inline-block px-4 py-2 rounded font-medium"
            style={{ backgroundColor: '#2563eb', color: '#fff' }}
          >
            {element.buttonText || 'Button'}
          </a>
        </div>
      );
    case 'list':
      return (
        <ul key={element.id} className="list-disc list-inside mb-3 text-sm" style={{ color }}>
          {(element.listItems || []).map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'video':
      return element.videoUrl ? (
        <div key={element.id} className="mb-3 aspect-video w-full max-w-full">
          <iframe title="Video" src={element.videoUrl} className="w-full h-full rounded" allowFullScreen />
        </div>
      ) : null;
    default:
      return null;
  }
}

const PublicSignupForm: React.FC = () => {
  const { formId: shortFormId } = useParams<{ formId: string }>();
  const [form, setForm] = useState<SignupForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<{ [fieldId: string]: any }>({});
  const [honeypot, setHoneypot] = useState('');
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    if (shortFormId) {
      loadForm();
      trackView();
    }
  }, [shortFormId]);

  const loadForm = async () => {
    if (!shortFormId) return;

    try {
      setLoading(true);
      const loadedForm = await signupFormService.getFormByShortId(shortFormId);
      if (loadedForm) {
        setForm(loadedForm);
        const blocksToRender = loadedForm.blocks || [];
        const initialData: { [key: string]: any } = {};
        blocksToRender.forEach((block) => {
          if (block.kind === 'field') {
            initialData[block.id] = ''; // defaultValue is ignored anyway
          }
        });
        setFormData(initialData);
      } else {
        setError('Form not found');
      }
    } catch (err) {
      console.error('Error loading form:', err);
      setError('Failed to load form');
    } finally {
      setLoading(false);
    }
  };

  const trackView = async () => {
    if (shortFormId) {
      await signupFormService.trackFormView(shortFormId);
    }
  };

  const handleInputChange = (fieldId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !shortFormId || submitting) return;

    setSubmitting(true);
    setError('');

    try {
      const result = await signupFormService.submitForm(
        shortFormId,
        formData,
        {
          source:
            typeof window !== 'undefined' && (window.location.search || '').includes('embed=1')
              ? 'shopify_storefront'
              : 'hosted',
          userAgent: navigator.userAgent,
          ipAddress: undefined,
        },
        honeypot
      );

      if (result.success) {
        setSubmitted(true);
        setPendingConfirmation(!!result.requiresConfirmation);
      } else {
        setError(result.message || form.settings.errorMessage);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(form.settings.errorMessage || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsClosed(true);
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'SIGNUP_FORM_CLOSED', formId: shortFormId }, '*');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  if (isClosed) {
    return null;
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div
          className="max-w-md w-full rounded-lg shadow-lg p-8 text-center"
          style={{
            backgroundColor: form.styling.backgroundColor,
            color: form.styling.textColor,
          }}
        >
          <div className="text-green-500 text-5xl mb-4">✓</div>
          <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
          {pendingConfirmation ? (
            <p className="text-lg">
              Please check your inbox and click the confirmation link to complete your subscription.
            </p>
          ) : (
            <p className="text-lg">{form.settings.successMessage || 'Thank you for subscribing!'}</p>
          )}
          {form.settings.thankYouRedirectUrl && !pendingConfirmation && (
            <a
              href={form.settings.thankYouRedirectUrl}
              className="inline-block mt-6 px-6 py-2 rounded-lg text-white transition-colors"
              style={{
                backgroundColor: form.styling.buttonColor,
                color: form.styling.buttonTextColor,
              }}
            >
              Continue
            </a>
          )}
        </div>
      </div>
    );
  }

  const blocks = form.blocks || [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div
        className="max-w-md w-full rounded-lg shadow-lg relative"
        style={{
          backgroundColor: form.styling.backgroundColor,
          padding: form.styling.padding,
          maxWidth: form.styling.maxWidth,
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-3 right-3 z-50 w-8 h-8 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800 transition-colors shadow-sm"
          style={{
            backgroundColor: form.styling.backgroundColor ? 'rgba(255, 255, 255, 0.9)' : 'rgba(229, 231, 235, 0.9)',
            backdropFilter: 'blur(4px)',
          }}
          title="Close form"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <form onSubmit={handleSubmit} className="space-y-4">
          {blocks.map((block) => {
            if (block.kind === 'element') {
              return renderContentBlock(block, form.styling.textColor);
            }
            if (block.kind === 'field') {
              return (
                <div key={block.id}>
                  {form.settings.showLabels && (
                    <label
                      htmlFor={block.id}
                      className="block text-sm font-medium mb-1"
                      style={{ color: form.styling.textColor }}
                    >
                      {block.label}
                      {block.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  )}
                  {block.fieldType === 'textarea' ? (
                    <textarea
                      id={block.id}
                      value={formData[block.id] || ''}
                      onChange={(e) => handleInputChange(block.id, e.target.value)}
                      placeholder={form.settings.showPlaceholders ? block.placeholder : ''}
                      required={block.required}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        borderColor: form.styling.borderColor,
                        borderRadius: form.styling.borderRadius,
                        color: form.styling.textColor,
                      }}
                      rows={4}
                    />
                  ) : block.fieldType === 'select' && block.options ? (
                    <select
                      id={block.id}
                      value={formData[block.id] || ''}
                      onChange={(e) => handleInputChange(block.id, e.target.value)}
                      required={block.required}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        borderColor: form.styling.borderColor,
                        borderRadius: form.styling.borderRadius,
                        color: form.styling.textColor,
                      }}
                    >
                      <option value="">Select {block.label}</option>
                      {block.options.map((option, i) => (
                        <option key={i} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  ) : block.fieldType === 'checkbox' ? (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData[block.id] || false}
                        onChange={(e) => handleInputChange(block.id, e.target.checked)}
                        required={block.required}
                        className="w-4 h-4"
                        style={{ accentColor: form.styling.buttonColor }}
                      />
                      <span style={{ color: form.styling.textColor }}>{block.label}</span>
                    </label>
                  ) : block.fieldType === 'date' ? (
                    <input
                      id={block.id}
                      type="date"
                      value={formData[block.id] || ''}
                      onChange={(e) => handleInputChange(block.id, e.target.value)}
                      required={block.required}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        borderColor: form.styling.borderColor,
                        borderRadius: form.styling.borderRadius,
                        color: form.styling.textColor,
                      }}
                    />
                  ) : (
                    <input
                      id={block.id}
                      type={block.fieldType === 'email' ? 'email' : block.fieldType === 'phone' ? 'tel' : 'text'}
                      value={formData[block.id] || ''}
                      onChange={(e) => handleInputChange(block.id, e.target.value)}
                      placeholder={form.settings.showPlaceholders ? block.placeholder : ''}
                      required={block.required}
                      className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      style={{
                        borderColor: form.styling.borderColor,
                        borderRadius: form.styling.borderRadius,
                        color: form.styling.textColor,
                      }}
                    />
                  )}
                </div>
              );
            }
            return null;
          })}

          {form.settings.gdprEnabled && (
            <div className="text-xs">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  required
                  className="mt-1 w-4 h-4"
                  style={{ accentColor: form.styling.buttonColor }}
                />
                <span style={{ color: form.styling.textColor }}>
                  {form.settings.gdprText ||
                    'I agree to receive marketing emails and understand I can unsubscribe at any time.'}
                </span>
              </label>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-4 py-2 rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: form.styling.buttonColor,
              color: form.styling.buttonTextColor,
              borderRadius: form.styling.borderRadius,
            }}
          >
            {submitting ? 'Submitting...' : form.settings.submitButtonText}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PublicSignupForm;
